import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Copy,
  CheckCheck,
  Upload,
  Download,
  History,
  Undo2,
  PauseCircle,
  PlayCircle,
  UserCheck,
  ScrollText,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FilterMultiSelect } from "@/components/ui/FilterMultiSelect";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { LoadMoreButton } from "@/components/ui/LoadMoreButton";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import {
  useOfficialFeeRequestsFeed,
  usePendingAcknowledgementCount,
  usePendingApprovalCount,
  usePendingPaymentCount,
  useApproveOfficialFee,
  useUploadOfficialFeePayment,
  useAcknowledgeOfficialFee,
  useWithdrawOfficialFee,
  useHoldOfficialFee,
  useRestoreOfficialFee,
  useCloneOfficialFee,
  useOfficialFeeHistory,
} from "../hooks/useOfficialFees";
import { FEE_APPROVAL_THRESHOLD } from "../utils/feeThreshold";
import { withdrawOthersConfirm } from "../utils/withdraw";
import { OfficialFeeStatusBadge } from "../components/OfficialFeeStatusBadge";
import { OfficialFeeRejectModal } from "../components/OfficialFeeRejectModal";
import { OfficialFeeMonthlyTotals } from "../components/OfficialFeeMonthlyTotals";
import { OfficialFeeOrderLink } from "../components/OfficialFeeOrderLink";
import {
  OFFICIAL_FEE_BADGE_COLORS,
  OFFICIAL_FEE_BADGE_LABELS,
  type OfficialFeeBadge,
} from "../badges";
import {
  OFFICIAL_FEE_HISTORY_VERB_LABELS,
  OFFICIAL_FEE_STATUS_LABELS,
  OFFICIAL_FEE_STATUS_ORDER,
} from "../types";
import type { OfficialFeeRequestList, OfficialFeeStatus } from "../types";

// Per-user persistence for the "Tylko moje opłaty" filter. Scoped by user id so the
// remembered state doesn't leak between accounts sharing a browser.
const ONLY_MINE_KEY_PREFIX = "elg5-official-fees-only-mine";

function onlyMineStorageKey(userId: string | undefined) {
  return `${ONLY_MINE_KEY_PREFIX}:${userId ?? "anon"}`;
}

function readOnlyMine(userId: string | undefined) {
  try {
    return localStorage.getItem(onlyMineStorageKey(userId)) === "1";
  } catch {
    return false;
  }
}

function writeOnlyMine(userId: string | undefined, value: boolean) {
  try {
    localStorage.setItem(onlyMineStorageKey(userId), value ? "1" : "0");
  } catch {
    /* ignore storage errors (private mode, quota) */
  }
}

type SortKey =
  | "office_name"
  | "project_short_name"
  | "amount"
  | "status"
  | "description"
  | "requested_by_name"
  | "created_at";

type SortDirection = "asc" | "desc";

/**
 * Column -> ?ordering= field. Only `status` differs: the column is ranked by workflow
 * position, not alphabetically, so the backend exposes that rank as `status_order`
 * (ordering by `status` itself would sort the raw string).
 */
const SORT_PARAM: Record<SortKey, string> = {
  office_name: "office_name",
  project_short_name: "project_short_name",
  amount: "amount",
  status: "status_order",
  description: "description",
  requested_by_name: "requested_by_name",
  created_at: "created_at",
};

// Default view: pending-approval on top (status rank), then newest first within each
// status. Reachable again via the "Resetuj sortowanie" button.
const DEFAULT_SORT_KEY: SortKey = "status";
const DEFAULT_SORT_DIRECTION: SortDirection = "asc";

/**
 * Build the ?ordering= value. Sorting by status is always tie-broken by newest-first
 * `-created_at`, so the default status view reads "pending on top, then most recent" —
 * every other column already sorts by a single, meaningful field.
 */
function buildOrdering(key: SortKey, direction: SortDirection): string {
  const primary = `${direction === "desc" ? "-" : ""}${SORT_PARAM[key]}`;
  return key === "status" ? `${primary},-created_at` : primary;
}

function formatAmount(amount: string) {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    // "always" forces a thousands separator for 4-digit pl-PL values; the pl-PL
    // CLDR default (minimumGroupingDigits=2) otherwise skips grouping below 10 000.
    useGrouping: "always",
  }).format(parseFloat(amount));
}

function formatDate(dt: string) {
  return new Date(dt).toLocaleDateString("pl-PL");
}

function formatDateTime(dt: string) {
  return new Date(dt).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" });
}

const NON_WITHDRAWABLE: OfficialFeeStatus[] = ["rejected", "payment_uploaded", "acknowledged", "withdrawn"];

/* Opcje filtra „Status" — w porządku obiegu (ten sam, po którym sortuje kolumna),
   nie alfabetycznie. */
const STATUS_FILTER_OPTIONS = OFFICIAL_FEE_STATUS_ORDER.map((status) => ({
  value: status,
  label: OFFICIAL_FEE_STATUS_LABELS[status],
}));

const HOLD_HINT =
  "Wstrzymaj przed akceptacją — prośba zostaje widoczna, ale wypada z kolejki i nikt " +
  "jej nie zaakceptuje ani nie odrzuci, dopóki nie zostanie przywrócona";

function HistoryTooltip({ feeId }: { feeId: string }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const { data: history, isLoading } = useOfficialFeeHistory(show ? feeId : null);

  return (
    <div
      className="relative inline-block"
      onMouseEnter={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setPos({ x: rect.left, y: rect.top });
        setShow(true);
      }}
      onMouseLeave={() => setShow(false)}
    >
      <button
        title="Historia zgłoszenia"
        className="p-1 rounded hover:bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]"
        onClick={(e) => e.stopPropagation()}
      >
        <History size={15} />
      </button>
      {show &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: pos.x,
              top: pos.y - 8,
              transform: "translateY(-100%)",
            }}
            className="z-[9999] w-80 bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg shadow-xl p-3 text-xs pointer-events-none"
          >
            <p className="font-semibold text-[var(--color-text-primary)] mb-2">Historia zgłoszenia</p>
            {isLoading ? (
              <p className="text-[var(--color-text-secondary)]">Ładowanie...</p>
            ) : !history?.length ? (
              <p className="text-[var(--color-text-secondary)]">Brak historii.</p>
            ) : (
              <div className="space-y-1.5">
                {history.map((entry) => (
                  <div key={entry.id} className="flex items-start gap-2">
                    <span className="text-[var(--color-text-secondary)] whitespace-nowrap mt-0.5">
                      {formatDateTime(entry.timestamp)}
                    </span>
                    <div className="min-w-0">
                      <span className="font-medium text-[var(--color-text-primary)]">
                        {entry.actor_name}
                      </span>
                      <span className="text-[var(--color-text-secondary)] ml-1">
                        — {OFFICIAL_FEE_HISTORY_VERB_LABELS[entry.verb] ?? entry.verb}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

function BadgeDot({ color }: { color: "red" | "amber" }) {
  const cls = color === "amber" ? "bg-amber-400" : "bg-red-500";
  return <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${cls}`} />;
}

/* Legend for the sidebar badges of "Opłaty urzędowe" (same counters, same wording —
   see ../badges.ts). Clicking an entry filters the list to the rows behind that
   badge via the feed's `?badge=`; clicking again clears the filter. Mirrors
   TendersBadgeSummary on /tenders. */
function OfficialFeesBadgeSummary({
  activeFilter,
  onFilter,
}: {
  activeFilter: OfficialFeeBadge | null;
  onFilter: (f: OfficialFeeBadge | null) => void;
}) {
  const { user } = useAuthStore();
  // Same gates the backend applies to the counters (apps/core/nav_badges.py).
  const canApprove = !!(
    user?.role === "director" || user?.role === "coordinator" || user?.is_ceo
  );
  const canPay = !!user?.has_small_account_access;

  const { data: pendingApproval } = usePendingApprovalCount(canApprove);
  const { data: pendingPayment } = usePendingPaymentCount(canPay);
  const { data: pendingAcknowledgement } = usePendingAcknowledgementCount();

  const items: Array<{ badge: OfficialFeeBadge; count: number }> = [];
  if (canApprove) items.push({ badge: "pending_approval", count: pendingApproval ?? 0 });
  if (canPay) items.push({ badge: "pending_payment", count: pendingPayment ?? 0 });
  items.push({ badge: "pending_acknowledgement", count: pendingAcknowledgement ?? 0 });

  return (
    <div className="flex flex-wrap gap-3 mb-5">
      {items.map((item) => {
        const isActive = activeFilter === item.badge;
        return (
          <button
            key={item.badge}
            type="button"
            aria-pressed={isActive}
            onClick={() => onFilter(isActive ? null : item.badge)}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors cursor-pointer ${
              isActive
                ? "bg-gray-200 dark:bg-gray-600 border-gray-400 dark:border-gray-400"
                : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            <BadgeDot color={OFFICIAL_FEE_BADGE_COLORS[item.badge]} />
            <span className="font-semibold text-gray-900 dark:text-gray-100">{item.count}</span>
            <span className="text-gray-500 dark:text-gray-400">
              {OFFICIAL_FEE_BADGE_LABELS[item.badge]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function OfficialFeeRequestsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [rejectModal, setRejectModal] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });
  const [uploadModal, setUploadModal] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [bulkUploadFiles, setBulkUploadFiles] = useState<File[]>([]);
  const [bulkAckModal, setBulkAckModal] = useState(false);
  const [onlyMine, setOnlyMine] = useState(() => readOnlyMine(user?.id));
  const [onlyPowerOfAttorney, setOnlyPowerOfAttorney] = useState(false);
  const [statusFilter, setStatusFilter] = useState<OfficialFeeStatus[]>([]);
  const [badgeFilter, setBadgeFilter] = useState<OfficialFeeBadge | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>(DEFAULT_SORT_KEY);
  const [sortDirection, setSortDirection] = useState<SortDirection>(DEFAULT_SORT_DIRECTION);
  const isDefaultSort = sortKey === DEFAULT_SORT_KEY && sortDirection === DEFAULT_SORT_DIRECTION;

  const isDirectorOrCoord = !!(
    user?.role === "director" || user?.role === "coordinator" || user?.is_ceo
  );
  const canPay = user?.has_small_account_access;
  const isCeo = user?.is_ceo;
  const canAddFee = !!(canPay || isCeo);
  // Users who see more than their own requests (approvers, payers, accountants)
  const canSeeOthersFees = !!(isDirectorOrCoord || canPay || user?.role === "accountant");

  // Monthly totals under the list — technical director (and CEO, who sees every
  // financial view in the app). Nobody else gets a group-wide spend figure.
  const canSeeMonthlyTotals = !!(
    user?.is_ceo || (user?.role === "director" && user?.is_technical_director)
  );

  // `status` jedzie tablicą — useInfiniteFeed serializuje ją jako powtórzony klucz
  // (?status=a&status=b), czyli tak, jak czyta go MultipleChoiceFilter na backendzie.
  const filters = useMemo(
    () => ({
      only_mine: onlyMine && canSeeOthersFees ? "true" : undefined,
      is_power_of_attorney: onlyPowerOfAttorney ? "true" : undefined,
      status: statusFilter.length ? statusFilter : undefined,
      badge: badgeFilter ?? undefined,
      ordering: buildOrdering(sortKey, sortDirection),
    }),
    [
      onlyMine,
      canSeeOthersFees,
      onlyPowerOfAttorney,
      statusFilter,
      badgeFilter,
      sortKey,
      sortDirection,
    ],
  );

  // Same narrowing as the list, minus the sort — the order of rows cannot change a sum,
  // and keeping it out means clicking a column header does not refetch the totals.
  // Statusy idą osobno: sumy biorą tylko te z nich, które w ogóle wchodzą do sum.
  const summaryFilters = useMemo(
    () => ({
      only_mine: filters.only_mine,
      is_power_of_attorney: filters.is_power_of_attorney,
      badge: filters.badge,
    }),
    [filters.only_mine, filters.is_power_of_attorney, filters.badge],
  );

  const {
    items,
    isLoading,
    isPlaceholderData,
    total,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    fetchAll,
    isFetchingAll,
  } = useOfficialFeeRequestsFeed(filters);

  // Selection is keyed by id over the LOADED rows. Loading more only adds rows, so
  // selections survive that; a filter or sort change can drop a selected row out of the
  // loaded set entirely — and handleBulkUpload iterates selectedIds directly rather than
  // through `items`, so a stale id there would POST to a fee the user can no longer see.
  const filterKey = JSON.stringify(filters);
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filterKey]);

  const approveMut = useApproveOfficialFee();
  const uploadMut = useUploadOfficialFeePayment();
  const acknowledgeMut = useAcknowledgeOfficialFee();
  const withdrawMut = useWithdrawOfficialFee();
  const holdMut = useHoldOfficialFee();
  const restoreMut = useRestoreOfficialFee();
  const cloneMut = useCloneOfficialFee();

  // Koordynator decyduje (akceptuje/odrzuca) tylko o opłatach swoich projektów —
  // dla cudzych backend (CanApproveFeeForProject) i tak odpowie 403, więc
  // przyciski decyzji w ogóle się nie pokazują.
  function canDecide(fee: OfficialFeeRequestList): boolean {
    if (!isDirectorOrCoord) return false;
    if (user?.role !== "coordinator") return true;
    return fee.can_decide;
  }

  // Koordynator akceptuje tylko do progu — powyżej backend odpowie 403, więc
  // przycisk ustępuje miejsca informacji, na kogo prośba czeka. Odrzucenie zostaje.
  function canApprove(fee: OfficialFeeRequestList): boolean {
    if (!canDecide(fee)) return false;
    // Rola, nie flaga is_ceo — backend (fee_approval_denial) też patrzy na rolę.
    if (user?.role !== "coordinator") return true;
    return parseFloat(fee.amount) <= FEE_APPROVAL_THRESHOLD;
  }

  function canAcknowledge(fee: OfficialFeeRequestList): boolean {
    if (fee.status !== "payment_uploaded") return false;
    return user?.id === fee.requested_by || canAddFee;
  }

  // Mirror of the backend `withdraw`: a decider withdraws their own request at any
  // pre-payment state and anybody's once approved (reject is gone by then); somebody
  // else's pending one is rejected, not withdrawn.
  function canWithdraw(fee: OfficialFeeRequestList): boolean {
    if (NON_WITHDRAWABLE.includes(fee.status)) return false;
    if (isCeo) return true;
    const isOwn = user?.id === fee.requested_by;
    if (canDecide(fee) && (isOwn || fee.status === "approved")) return true;
    if (isOwn && (fee.status === "pending_approval" || fee.status === "on_hold")) return true;
    return false;
  }

  function handleWithdraw(fee: OfficialFeeRequestList) {
    // A withdrawn request cannot be brought back — ask before doing it to someone else's.
    if (user?.id !== fee.requested_by && !confirm(withdrawOthersConfirm(fee.requested_by_name))) {
      return;
    }
    withdrawMut.mutate(fee.id);
  }

  // Wstrzymuje i przywraca składający ALBO ktokolwiek, kto o prośbie decyduje —
  // lustro `_may_pause` z backendu. Bez limitu kwoty: `canDecide`, nie `canApprove`,
  // bo wstrzymanie (jak odrzucenie) nie rusza pieniędzy.
  function canPause(fee: OfficialFeeRequestList): boolean {
    return user?.id === fee.requested_by || !!isCeo || canDecide(fee);
  }

  function canHold(fee: OfficialFeeRequestList): boolean {
    return fee.status === "pending_approval" && canPause(fee);
  }

  function canRestore(fee: OfficialFeeRequestList): boolean {
    return fee.status === "on_hold" && canPause(fee);
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleBulkUpload() {
    if (!bulkUploadFiles.length || selectedIds.size < 2) return;
    await Promise.all(
      Array.from(selectedIds).map((id) => uploadMut.mutateAsync({ id, files: bulkUploadFiles }))
    );
    setBulkUploadModal(false);
    setBulkUploadFiles([]);
    setSelectedIds(new Set());
  }

  async function handleBulkAcknowledge() {
    const idsToAck = items
      .filter((f) => selectedIds.has(f.id) && canAcknowledge(f))
      .map((f) => f.id);
    if (idsToAck.length < 2) return;
    await Promise.all(idsToAck.map((id) => acknowledgeMut.mutateAsync(id)));
    setBulkAckModal(false);
    setSelectedIds(new Set());
  }

  async function handleClone(fee: OfficialFeeRequestList) {
    const formData = await cloneMut.mutateAsync(fee.id);
    navigate("/official-fees/new", { state: { prefill: formData } });
  }

  async function handleUpload() {
    if (!uploadModal.id || !uploadFiles.length) return;
    await uploadMut.mutateAsync({ id: uploadModal.id, files: uploadFiles });
    setUploadModal({ open: false, id: null });
    setUploadFiles([]);
  }

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection(key === "created_at" ? "desc" : "asc");
    }
  }

  function resetSort() {
    setSortKey(DEFAULT_SORT_KEY);
    setSortDirection(DEFAULT_SORT_DIRECTION);
  }

  function SortableHeader({ label, columnKey }: { label: string; columnKey: SortKey }) {
    const active = sortKey === columnKey;
    return (
      <th className="text-left px-4 py-3 font-medium">
        <button
          className="inline-flex items-center gap-1 hover:text-[var(--color-text-primary)]"
          onClick={() => handleSort(columnKey)}
        >
          {label}
          {active ? (
            sortDirection === "asc" ? (
              <ArrowUp size={13} />
            ) : (
              <ArrowDown size={13} />
            )
          ) : (
            <ArrowUpDown size={13} className="opacity-40" />
          )}
        </button>
      </th>
    );
  }

  if (isLoading) return <PageLoader />;

  const selectedApprovedFees = items.filter(
    (f) => selectedIds.has(f.id) && f.status === "approved"
  );
  const selectedApprovedCount = selectedApprovedFees.length;
  const approvedOfficeNames = new Set(selectedApprovedFees.map((f) => f.office_name));
  const approvedSameOffice = approvedOfficeNames.size === 1;
  const showBulkButton = !!(canPay && selectedApprovedCount >= 2 && approvedSameOffice);
  const showBulkOfficeWarning = !!(canPay && selectedApprovedCount >= 2 && !approvedSameOffice);
  const selectedAckCount = items.filter(
    (f) => selectedIds.has(f.id) && canAcknowledge(f)
  ).length;
  const showBulkAckButton = selectedAckCount >= 2;

  return (
    <div className="p-2 sm:p-3 max-w-[1600px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
          Opłaty urzędowe
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          {!isDefaultSort && (
            <Button variant="ghost" onClick={resetSort} title="Przywróć domyślne sortowanie">
              <RotateCcw size={16} className="mr-1" />
              Resetuj sortowanie
            </Button>
          )}
          <FilterMultiSelect
            label="Status"
            options={STATUS_FILTER_OPTIONS}
            selected={statusFilter}
            onChange={(values) => setStatusFilter(values as OfficialFeeStatus[])}
          />
          {statusFilter.length > 0 && (
            <Button variant="ghost" onClick={() => setStatusFilter([])} title="Wyczyść filtr statusów">
              <RotateCcw size={16} className="mr-1" />
              Wyczyść status
            </Button>
          )}
          {canSeeOthersFees && (
            <label className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] cursor-pointer select-none mr-2">
              <input
                type="checkbox"
                checked={onlyMine}
                onChange={(e) => {
                  setOnlyMine(e.target.checked);
                  writeOnlyMine(user?.id, e.target.checked);
                }}
                className="rounded border-[var(--color-border)] cursor-pointer accent-[var(--color-primary)]"
              />
              Tylko moje opłaty
            </label>
          )}
          <label className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] cursor-pointer select-none mr-2">
            <input
              type="checkbox"
              checked={onlyPowerOfAttorney}
              onChange={(e) => setOnlyPowerOfAttorney(e.target.checked)}
              className="rounded border-[var(--color-border)] cursor-pointer accent-[var(--color-primary)]"
            />
            Opłaty za pełnomocnictwo
          </label>
          {showBulkAckButton && (
            <Button variant="secondary" onClick={() => setBulkAckModal(true)}>
              <CheckCheck size={16} className="mr-1" />
              Zbiorcze potwierdzenie odbioru ({selectedAckCount})
            </Button>
          )}
          {showBulkButton && (
            <Button variant="secondary" onClick={() => setBulkUploadModal(true)}>
              <Upload size={16} className="mr-1" />
              Prześlij zbiorcze potwierdzenie ({selectedApprovedCount})
            </Button>
          )}
          {showBulkOfficeWarning && (
            <span className="text-xs text-amber-600 max-w-[200px] leading-tight">
              Zbiorcze potwierdzenie wymaga opłat do tego samego urzędu
            </span>
          )}
          {canAddFee && (
            <Button variant="secondary" onClick={() => navigate("/official-fees/add")}>
              <Plus size={16} className="mr-1" />
              Dodaj opłatę
            </Button>
          )}
          <Button onClick={() => navigate("/official-fees/new")}>
            <Plus size={16} className="mr-1" />
            Nowa prośba
          </Button>
        </div>
      </div>

      <OfficialFeesBadgeSummary activeFilter={badgeFilter} onFilter={setBadgeFilter} />

      {items.length === 0 ? (
        <p className="text-[var(--color-text-secondary)] text-sm">
          {statusFilter.length > 0
            ? "Brak opłat o wybranym statusie."
            : badgeFilter
              ? "Brak opłat pasujących do wybranej plakietki."
              : onlyMine
                ? "Brak Twoich prośb o opłatę urzędową."
                : "Brak prośb o opłatę urzędową."}
        </p>
      ) : (
        <div className={isPlaceholderData ? "opacity-60 pointer-events-none" : undefined}>
          {/* Mobile: card list */}
          <ul className="md:hidden flex flex-col gap-2">
            {items.map((fee) => (
              <OfficialFeeCard
                key={fee.id}
                fee={fee}
                canDecideFn={canDecide}
                canPay={!!canPay}
                canApproveFn={canApprove}
                canAcknowledgeFn={canAcknowledge}
                canWithdrawFn={canWithdraw}
                canHoldFn={canHold}
                canRestoreFn={canRestore}
                onApprove={() => approveMut.mutate(fee.id)}
                onReject={() => setRejectModal({ open: true, id: fee.id })}
                onUpload={() => setUploadModal({ open: true, id: fee.id })}
                onAcknowledge={() => acknowledgeMut.mutate(fee.id)}
                onWithdraw={() => handleWithdraw(fee)}
                onHold={() => holdMut.mutate(fee.id)}
                onRestore={() => restoreMut.mutate(fee.id)}
                onClone={() => handleClone(fee)}
              />
            ))}
          </ul>
          {/* Desktop: table */}
          <div className="hidden md:block rounded-lg border border-[var(--color-border)] overflow-hidden bg-[var(--color-bg-surface)]">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1000px]">
              <thead className="bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]">
              <tr>
                <th className="px-4 py-3 w-8" />
                <th className="text-left px-4 py-3 font-medium">Lp.</th>
                <SortableHeader label="Urząd" columnKey="office_name" />
                <SortableHeader label="Projekt" columnKey="project_short_name" />
                <SortableHeader label="Kwota" columnKey="amount" />
                <SortableHeader label="Status" columnKey="status" />
                <SortableHeader label="Opis" columnKey="description" />
                <SortableHeader label="Wnioskujący" columnKey="requested_by_name" />
                <SortableHeader label="Data" columnKey="created_at" />
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {items.map((fee, idx) => (
                <tr
                  key={fee.id}
                  className="hover:bg-[var(--color-bg-elevated)] cursor-pointer"
                  onClick={() => navigate(`/official-fees/${fee.id}`)}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {((canPay && fee.status === "approved") || canAcknowledge(fee)) && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(fee.id)}
                        onChange={() => toggleSelect(fee.id)}
                        className="rounded border-[var(--color-border)] cursor-pointer accent-[var(--color-primary)]"
                      />
                    )}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-[var(--color-text-primary)]">
                    <div className="flex items-center gap-1.5">
                      {fee.office_name}
                      {fee.investor_name_required_in_title && (
                        <span
                          title="Wymagana nazwa inwestora w tytule"
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200 shrink-0"
                        >
                          <UserCheck size={10} />
                          inwestor
                        </span>
                      )}
                      {fee.is_power_of_attorney && (
                        <span
                          title="Opłata za pełnomocnictwo"
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700 border border-violet-200 shrink-0"
                        >
                          <ScrollText size={10} />
                          pełnomocnictwo
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {fee.project_short_name}
                    {fee.order && fee.order_number && (
                      <div className="text-xs mt-0.5 max-w-[200px] break-words">
                        <span className="text-[var(--color-text-muted)]">Zamówienie </span>
                        <OfficialFeeOrderLink orderId={fee.order} orderNumber={fee.order_number} />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-[var(--color-text-primary)]">
                    {formatAmount(fee.amount)}
                  </td>
                  <td className="px-4 py-3">
                    <OfficialFeeStatusBadge status={fee.status} />
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)] max-w-[180px]">
                    <span className="block truncate" title={fee.description}>
                      {fee.description || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {fee.requested_by_name}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {formatDate(fee.created_at)}
                  </td>
                  <td
                    className="px-4 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex flex-col items-start gap-1">
                      {canDecide(fee) && fee.status === "pending_approval" && (
                        <div className="flex items-center gap-1.5">
                          {canApprove(fee) ? (
                            <Button
                              size="sm"
                              onClick={() => approveMut.mutate(fee.id)}
                              disabled={approveMut.isPending}
                            >
                              Akceptuj
                            </Button>
                          ) : (
                            <span
                              className="text-xs text-[var(--color-text-secondary)] whitespace-nowrap"
                              title={`Kwota powyżej ${FEE_APPROVAL_THRESHOLD} zł — akceptuje dyrektor`}
                            >
                              Akceptuje dyrektor
                            </span>
                          )}
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => {
                              setRejectModal({ open: true, id: fee.id });
                            }}
                          >
                            Odrzuć
                          </Button>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        {canPay && fee.status === "approved" && (
                          <button
                            title="Prześlij potwierdzenie"
                            className="p-1 rounded hover:bg-blue-100 text-blue-600"
                            onClick={() => setUploadModal({ open: true, id: fee.id })}
                          >
                            <Upload size={15} />
                          </button>
                        )}
                        {canAcknowledge(fee) && (
                          <button
                            title="Oznacz jako przetworzone"
                            className="p-1 rounded hover:bg-green-100 text-green-600"
                            onClick={() => acknowledgeMut.mutate(fee.id)}
                          >
                            <CheckCheck size={15} />
                          </button>
                        )}
                        {canHold(fee) && (
                          <button
                            title={HOLD_HINT}
                            className="p-1 rounded hover:bg-orange-100 text-orange-600"
                            onClick={() => holdMut.mutate(fee.id)}
                            disabled={holdMut.isPending}
                          >
                            <PauseCircle size={15} />
                          </button>
                        )}
                        {canRestore(fee) && (
                          <button
                            title="Przywróć do akceptacji"
                            className="p-1 rounded hover:bg-green-100 text-green-600"
                            onClick={() => restoreMut.mutate(fee.id)}
                            disabled={restoreMut.isPending}
                          >
                            <PlayCircle size={15} />
                          </button>
                        )}
                        {canWithdraw(fee) && (
                          <button
                            title="Wycofaj zgłoszenie"
                            className="p-1 rounded hover:bg-orange-100 text-orange-600"
                            onClick={() => handleWithdraw(fee)}
                            disabled={withdrawMut.isPending}
                          >
                            <Undo2 size={15} />
                          </button>
                        )}
                        {fee.payment_confirmation_files?.map((f) =>
                          f.url ? (
                            <a
                              key={f.id}
                              href={f.url}
                              download={f.original_filename}
                              title={`Pobierz: ${f.original_filename}`}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Download size={12} />
                              PDF
                            </a>
                          ) : null
                        )}
                        <button
                          title="Kopiuj prośbę"
                          className="p-1 rounded hover:bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]"
                          onClick={() => handleClone(fee)}
                        >
                          <Copy size={15} />
                        </button>
                        <HistoryTooltip feeId={fee.id} />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </div>
          <LoadMoreButton
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
            onLoadAll={fetchAll}
            isFetchingAll={isFetchingAll}
            loaded={items.length}
            total={total}
          />
        </div>
      )}

      {canSeeMonthlyTotals && (
        <OfficialFeeMonthlyTotals filters={summaryFilters} statuses={statusFilter} />
      )}

      {/* Reject modal */}
      <OfficialFeeRejectModal
        feeId={rejectModal.id}
        open={rejectModal.open}
        onClose={() => setRejectModal({ open: false, id: null })}
      />

      {/* Bulk acknowledge modal */}
      <Modal
        open={bulkAckModal}
        onClose={() => setBulkAckModal(false)}
        title="Zbiorcze potwierdzenie odbioru"
      >
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-text-secondary)]">
            Potwierdzasz odbiór <strong>{selectedAckCount}</strong> opłat ze statusem „Potwierdzenie przesłane".
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setBulkAckModal(false)}>
              Anuluj
            </Button>
            <Button
              disabled={acknowledgeMut.isPending}
              onClick={handleBulkAcknowledge}
            >
              Potwierdź odbiór wszystkich
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk upload payment modal */}
      <Modal
        open={bulkUploadModal}
        onClose={() => {
          setBulkUploadModal(false);
          setBulkUploadFiles([]);
        }}
        title="Prześlij zbiorcze potwierdzenie przelewu (PDF)"
      >
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-text-secondary)]">
            Wybrano <strong>{selectedApprovedCount}</strong> opłaty zaakceptowane do urzędu{" "}
            <strong>{Array.from(approvedOfficeNames)[0]}</strong>. Ten sam plik zostanie przesłany do każdej z nich.
          </p>
          <FileDropZone
            files={bulkUploadFiles}
            onFilesChange={setBulkUploadFiles}
            multiple
            accept=".pdf"
            label="Przeciągnij pliki PDF lub kliknij, aby wybrać"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setBulkUploadModal(false);
                setBulkUploadFiles([]);
              }}
            >
              Anuluj
            </Button>
            <Button
              disabled={!bulkUploadFiles.length || uploadMut.isPending}
              onClick={handleBulkUpload}
            >
              Prześlij do wszystkich zaznaczonych
            </Button>
          </div>
        </div>
      </Modal>

      {/* Upload payment modal */}
      <Modal
        open={uploadModal.open}
        onClose={() => {
          setUploadModal({ open: false, id: null });
          setUploadFiles([]);
        }}
        title="Prześlij potwierdzenia przelewu (PDF)"
      >
        <div className="space-y-4">
          <FileDropZone
            files={uploadFiles}
            onFilesChange={setUploadFiles}
            multiple
            accept=".pdf"
            label="Przeciągnij pliki PDF lub kliknij, aby wybrać"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setUploadModal({ open: false, id: null });
                setUploadFiles([]);
              }}
            >
              Anuluj
            </Button>
            <Button
              disabled={!uploadFiles.length || uploadMut.isPending}
              onClick={handleUpload}
            >
              Prześlij
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Mobile card ───────────────────────────────────────────────────────────────

interface OfficialFeeCardProps {
  fee: OfficialFeeRequestList;
  canDecideFn: (fee: OfficialFeeRequestList) => boolean;
  canPay: boolean;
  canApproveFn: (fee: OfficialFeeRequestList) => boolean;
  canAcknowledgeFn: (fee: OfficialFeeRequestList) => boolean;
  canWithdrawFn: (fee: OfficialFeeRequestList) => boolean;
  canHoldFn: (fee: OfficialFeeRequestList) => boolean;
  canRestoreFn: (fee: OfficialFeeRequestList) => boolean;
  onApprove: () => void;
  onReject: () => void;
  onUpload: () => void;
  onAcknowledge: () => void;
  onWithdraw: () => void;
  onHold: () => void;
  onRestore: () => void;
  onClone: () => void;
}

function OfficialFeeCard({
  fee,
  canDecideFn,
  canPay,
  canApproveFn,
  canAcknowledgeFn,
  canWithdrawFn,
  canHoldFn,
  canRestoreFn,
  onApprove,
  onReject,
  onUpload,
  onAcknowledge,
  onWithdraw,
  onHold,
  onRestore,
  onClone,
}: OfficialFeeCardProps) {
  const navigate = useNavigate();

  return (
    <li
      className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3 shadow-sm cursor-pointer active:bg-[var(--color-bg-elevated)]"
      onClick={() => navigate(`/official-fees/${fee.id}`)}
    >
      {/* Status + investor / power-of-attorney tags */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <OfficialFeeStatusBadge status={fee.status} />
        <div className="flex items-center gap-1">
          {fee.investor_name_required_in_title && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200 shrink-0">
              <UserCheck size={10} />
              inwestor
            </span>
          )}
          {fee.is_power_of_attorney && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700 border border-violet-200 shrink-0">
              <ScrollText size={10} />
              pełnomocnictwo
            </span>
          )}
        </div>
      </div>

      {/* Office + project */}
      <p className="font-semibold text-[var(--color-text-primary)] text-sm mb-0.5">{fee.office_name}</p>
      {fee.project_short_name && (
        <p className="text-xs text-[var(--color-text-secondary)] mb-1">{fee.project_short_name}</p>
      )}
      {fee.order && fee.order_number && (
        <p className="text-xs text-[var(--color-text-secondary)] mb-1 break-words">
          Zamówienie <OfficialFeeOrderLink orderId={fee.order} orderNumber={fee.order_number} />
        </p>
      )}

      {/* Amount + date */}
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-sm font-medium text-[var(--color-text-primary)]">
          {formatAmount(fee.amount)}
        </span>
        <span className="text-xs text-[var(--color-text-secondary)]">{formatDate(fee.created_at)}</span>
      </div>

      {/* Description */}
      {fee.description && (
        <p className="text-xs text-[var(--color-text-secondary)] italic mb-1.5 line-clamp-2">{fee.description}</p>
      )}

      {/* Requester */}
      <p className="text-xs text-[var(--color-text-secondary)] mb-3">{fee.requested_by_name}</p>

      {/* Actions */}
      <div
        className="flex flex-wrap gap-2 pt-2.5 border-t border-[var(--color-border)]"
        onClick={(e) => e.stopPropagation()}
      >
        {canDecideFn(fee) && fee.status === "pending_approval" && (
          <>
            {canApproveFn(fee) ? (
              <Button size="sm" onClick={onApprove} className="flex-1">Akceptuj</Button>
            ) : (
              <span className="flex-1 text-xs text-[var(--color-text-secondary)] self-center">
                Akceptuje dyrektor
              </span>
            )}
            <Button size="sm" variant="danger" onClick={onReject} className="flex-1">Odrzuć</Button>
          </>
        )}
        {canPay && fee.status === "approved" && (
          <button
            onClick={onUpload}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-blue-50 text-blue-600 border border-blue-200"
          >
            <Upload size={13} /> Prześlij
          </button>
        )}
        {canAcknowledgeFn(fee) && (
          <button
            onClick={onAcknowledge}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-green-50 text-green-600 border border-green-200"
          >
            <CheckCheck size={13} /> Potwierdź odbiór
          </button>
        )}
        {canHoldFn(fee) && (
          <button
            onClick={onHold}
            title={HOLD_HINT}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-orange-50 text-orange-600 border border-orange-200"
          >
            <PauseCircle size={13} /> Wstrzymaj
          </button>
        )}
        {canRestoreFn(fee) && (
          <button
            onClick={onRestore}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-green-50 text-green-600 border border-green-200"
          >
            <PlayCircle size={13} /> Przywróć
          </button>
        )}
        {canWithdrawFn(fee) && (
          <button
            onClick={onWithdraw}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-orange-50 text-orange-600 border border-orange-200"
          >
            <Undo2 size={13} /> Wycofaj
          </button>
        )}
        {fee.payment_confirmation_files?.map((f) =>
          f.url ? (
            <a
              key={f.id}
              href={f.url}
              download={f.original_filename}
              title={`Pobierz: ${f.original_filename}`}
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              <Download size={12} /> PDF
            </a>
          ) : null
        )}
        <button
          onClick={onClone}
          title="Kopiuj prośbę"
          className="p-1.5 rounded border border-[var(--color-border)] text-[var(--color-text-secondary)]"
        >
          <Copy size={14} />
        </button>
      </div>
    </li>
  );
}
